export type ProfileAxis = 'x' | 'y' | 'z';
export type ProfileOperation = 'none' | 'fillet' | 'chamfer';
type ProfileTool = 'move' | 'edgeMove' | 'fillet' | 'chamfer';

export interface ProfilePoint {
  id: number;
  u: number;
  v: number;
  operation: ProfileOperation;
  size: number;
}

interface ProfileEdge {
  a: number;
  b: number;
}

interface ProfileSnapshot {
  points: ProfilePoint[];
  edges: ProfileEdge[];
}

export interface ProfileInspectorOpenOptions {
  moduleId: string;
  part: string;
  axis: ProfileAxis;
  position: number;
  segments: Float32Array;
}

function axisCoordinates(x: number, y: number, z: number, axis: ProfileAxis): [number, number] {
  if (axis === 'x') return [y, z];
  if (axis === 'y') return [x, z];
  return [x, y];
}

function pointKey(u: number, v: number): string {
  return `${u.toFixed(4)},${v.toFixed(4)}`;
}

function profileFromSegments(segments: Float32Array, axis: ProfileAxis): { points: ProfilePoint[]; edges: ProfileEdge[] } {
  const points: ProfilePoint[] = [];
  const ids = new Map<string, number>();
  const edges: ProfileEdge[] = [];
  const add = (u: number, v: number): number => {
    const key = pointKey(u, v);
    const existing = ids.get(key);
    if (existing !== undefined) return existing;
    const id = points.length + 1;
    ids.set(key, id);
    points.push({ id, u, v, operation: 'none', size: 1 });
    return id;
  };
  for (let i = 0; i + 5 < segments.length; i += 6) {
    const a = axisCoordinates(segments[i]!, segments[i + 1]!, segments[i + 2]!, axis);
    const b = axisCoordinates(segments[i + 3]!, segments[i + 4]!, segments[i + 5]!, axis);
    const aId = add(a[0], a[1]);
    const bId = add(b[0], b[1]);
    if (aId !== bId) edges.push({ a: aId, b: bId });
  }
  return { points, edges };
}

function formatNumber(value: number): string {
  return Number.isFinite(value) ? value.toFixed(2).replace(/\.00$/, '') : '0';
}

export function createProfileInstruction(
  options: ProfileInspectorOpenOptions,
  points: ProfilePoint[],
  selectedId: number | null,
  baselinePoints: ProfilePoint[] = [],
  symmetryDetected = false,
  selectedEdge: ProfileEdge | null = null,
): string {
  const selected = points.find((point) => point.id === selectedId);
  const operations = points
    .filter((point) => point.operation !== 'none')
    .map((point) => `${point.operation} P${point.id} ${formatNumber(point.size)}mm`)
    .join(', ');
  const pointList = points
    .map((point) => `P${point.id}=(${formatNumber(point.u)}, ${formatNumber(point.v)})`)
    .join('; ');
  const selectedText = selected
    ? `Selected P${selected.id} at (${formatNumber(selected.u)}, ${formatNumber(selected.v)}), operation=${selected.operation}, size=${formatNumber(selected.size)}mm.`
    : 'No point selected.';
  const selectedEdgeText = selectedEdge
    ? `Selected edge: P${selectedEdge.a} -> P${selectedEdge.b}.`
    : 'No edge selected.';
  const edgeList = edgesFromPointList(points, options.axis, selectedEdge);
  const uValues = points.map((point) => point.u);
  const vValues = points.map((point) => point.v);
  const baseline = new Map(baselinePoints.map((point) => [point.id, point]));
  const current = new Map(points.map((point) => [point.id, point]));
  const changes: string[] = [];
  for (const point of points) {
    const before = baseline.get(point.id);
    if (!before) {
      changes.push(`ADDED P${point.id}=(${formatNumber(point.u)}, ${formatNumber(point.v)})`);
      continue;
    }
    const moved = Math.hypot(point.u - before.u, point.v - before.v) > 0.01;
    const operationChanged = point.operation !== before.operation || Math.abs(point.size - before.size) > 0.01;
    if (moved) changes.push(`MOVED P${point.id}: (${formatNumber(before.u)}, ${formatNumber(before.v)}) -> (${formatNumber(point.u)}, ${formatNumber(point.v)})`);
    if (operationChanged) changes.push(`UPDATED P${point.id}: ${before.operation}/${formatNumber(before.size)}mm -> ${point.operation}/${formatNumber(point.size)}mm`);
  }
  for (const point of baselinePoints) {
    if (!current.has(point.id)) changes.push(`REMOVED P${point.id}`);
  }
  return [
    'AI PROFILE DRAWING REQUEST v1',
    'Drawing type: editable 2D cross-section/profile extracted from a 3D solid.',
    `module=${options.moduleId}; part=${options.part}; section_axis=${options.axis.toUpperCase()}; section_position=${formatNumber(options.position)}mm`,
    `coordinate_system: u,v in millimetres; u_range=${formatNumber(Math.min(...uValues))}..${formatNumber(Math.max(...uValues))}; v_range=${formatNumber(Math.min(...vValues))}..${formatNumber(Math.max(...vValues))}`,
    `topology: ${edgeList || '(no edges)'}; point_count=${points.length}; edge_count=${points.length ? edgeList.split(' -> ').length - 1 : 0}`,
    'Profile semantics: This is an editable cross-section of the selected solid. Apply the edited profile according to the module construction method (for example revolve, extrude, sweep, loft, or a rectangular/planar section); do not interpret it as an isolated flat outline.',
    symmetryDetected
      ? 'Symmetry detected around the profile centerline u=0. Treat one-side edits as the primary design intent and mirror them to the matching opposite side; do not require separate independent instructions for both sides.'
      : 'No reliable bilateral symmetry was detected; preserve the existing asymmetry unless explicitly changed.',
    selectedText,
    selectedEdgeText,
    `changes_from_opened_state: ${changes.join('; ') || 'none'}`,
    `points: ${pointList || '(none)'}`,
    `corner_operations: ${operations || 'none'}`,
    'Requested change: ',
    'Keep all unmentioned geometry unchanged. Apply the requested operation to the selected profile point/edge and preserve watertight solids.',
    'Interpretation note: The primary purpose of this request is to communicate design intent. Listed points, coordinates, and deltas are references for locating the affected region, not isolated rigid commands. Preserve continuity, symmetry when appropriate, wall thickness, and the surrounding shape.',
  ].join('\n');
}

function edgesFromPointList(points: ProfilePoint[], axis: ProfileAxis, selectedEdge: ProfileEdge | null): string {
  const pointById = new Map(points.map((point) => [point.id, point]));
  const edges = points.slice(0, -1).map((point, index) => {
    const next = points[index + 1]!;
    return `P${point.id}(${formatNumber(point.u)},${formatNumber(point.v)}) -> P${next.id}(${formatNumber(next.u)},${formatNumber(next.v)})`;
  });
  if (selectedEdge) {
    const a = pointById.get(selectedEdge.a);
    const b = pointById.get(selectedEdge.b);
    if (a && b) edges.push(`SELECTED P${a.id}(${formatNumber(a.u)},${formatNumber(a.v)}) -> P${b.id}(${formatNumber(b.u)},${formatNumber(b.v)})`);
  }
  return edges.join(' -> ') || `axis=${axis.toUpperCase()}`;
}

export class ProfileInspector {
  public readonly el: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private title: HTMLElement;
  private readout: HTMLElement;
  private sizeInput: HTMLInputElement;
  private addPointButton: HTMLButtonElement;
  private copyButton: HTMLButtonElement;
  private closeButton: HTMLButtonElement;
  private resetButton: HTMLButtonElement;
  private undoButton: HTMLButtonElement;
  private redoButton: HTMLButtonElement;
  private toolButtons = new Map<ProfileTool, HTMLButtonElement>();
  private options: ProfileInspectorOpenOptions | null = null;
  private points: ProfilePoint[] = [];
  private initialPoints: ProfilePoint[] = [];
  private edges: ProfileEdge[] = [];
  private initialEdges: ProfileEdge[] = [];
  private symmetryPairs = new Map<number, number>();
  private symmetryDetected = false;
  private history: ProfileSnapshot[] = [];
  private historyIndex = -1;
  private viewZoom = 1;
  private viewPanX = 0;
  private viewPanY = 0;
  private panning = false;
  private panStart: [number, number] | null = null;
  private activeTool: ProfileTool = 'move';
  private toolDragStart: [number, number] | null = null;
  private toolDragStartScreen: [number, number] | null = null;
  private toolStartSize = 1;
  private selectedId: number | null = null;
  private draggingId: number | null = null;
  private draggingEdge: ProfileEdge | null = null;
  private selectedEdge: ProfileEdge | null = null;
  private lastPointer: [number, number] | null = null;
  private addPointMode = false;
  private bounds = { minU: -1, maxU: 1, minV: -1, maxV: 1 };

  constructor() {
    this.el = document.createElement('div');
    this.el.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,.35);z-index:1000;display:none;align-items:center;justify-content:center;font-family:var(--mono-font,monospace);';
    const card = document.createElement('div');
    card.style.cssText = 'width:min(920px,calc(100vw - 28px));height:min(720px,calc(100vh - 28px));background:#fff;border:1px solid #dbe3ef;border-radius:12px;box-shadow:0 18px 60px rgba(15,23,42,.28);display:flex;flex-direction:column;overflow:hidden;';
    const header = document.createElement('div');
    header.style.cssText = 'display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1px solid #e5e7eb;';
    this.title = document.createElement('strong');
    this.title.textContent = 'Profile Inspector';
    this.title.style.cssText = 'flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
    this.undoButton = this.makeButton('Undo', '#f8fafc');
    this.undoButton.title = 'Undo the last profile edit';
    this.undoButton.addEventListener('click', () => this.undo());
    this.redoButton = this.makeButton('Redo', '#f8fafc');
    this.redoButton.title = 'Redo the last undone profile edit';
    this.redoButton.addEventListener('click', () => this.redo());
    this.resetButton = this.makeButton('Reset', '#f8fafc');
    this.resetButton.title = 'Restore the profile as it was when opened';
    this.resetButton.addEventListener('click', () => this.reset());
    this.closeButton = this.makeButton('Close', '#f8fafc');
    this.closeButton.addEventListener('click', () => this.close());
    header.append(this.title, this.undoButton, this.redoButton, this.resetButton, this.closeButton);
    const body = document.createElement('div');
    body.style.cssText = 'display:flex;min-height:0;flex:1;';
    this.canvas = document.createElement('canvas');
    this.canvas.width = 760;
    this.canvas.height = 560;
    this.canvas.style.cssText = 'min-width:0;flex:1;background:#f8fafc;cursor:crosshair;';
    this.ctx = this.canvas.getContext('2d')!;
    const side = document.createElement('div');
    side.style.cssText = 'width:245px;padding:14px;border-left:1px solid #e5e7eb;display:flex;flex-direction:column;gap:10px;overflow:auto;';
    const help = document.createElement('div');
    help.textContent = 'Kéo điểm để sửa profile. Bật Add point rồi click lên cạnh. Chọn điểm để đặt bo/vát.';
    help.style.cssText = 'font-size:11px;line-height:1.45;color:#64748b;';
    this.readout = document.createElement('div');
    this.readout.style.cssText = 'font-size:11px;line-height:1.6;color:#334155;background:#f8fafc;border-radius:6px;padding:8px;white-space:pre-line;';
    this.sizeInput = document.createElement('input');
    this.sizeInput.type = 'number';
    this.sizeInput.min = '0';
    this.sizeInput.step = '0.1';
    this.sizeInput.value = '1';
    this.sizeInput.title = 'Fillet radius or chamfer size for the active tool';
    this.sizeInput.addEventListener('change', () => this.applySelectedSize());
    this.addPointButton = this.makeButton('Add point on edge', '#eff6ff');
    this.addPointButton.textContent = '＋ Add';
    this.addPointButton.style.flex = '1 1 0';
    this.addPointButton.style.padding = '7px 4px';
    this.addPointButton.addEventListener('click', () => {
      this.setActiveTool('move');
      this.addPointMode = !this.addPointMode;
      this.addPointButton.style.background = this.addPointMode ? '#2563eb' : '#eff6ff';
      this.addPointButton.style.color = this.addPointMode ? '#fff' : '#1d4ed8';
      this.canvas.style.cursor = this.addPointMode ? 'crosshair' : 'crosshair';
    });
    this.copyButton = this.makeButton('Copy for AI', '#2563eb');
    this.copyButton.style.color = '#fff';
    this.copyButton.addEventListener('click', async () => {
      if (!this.options) return;
      const text = createProfileInstruction(this.options, this.points, this.selectedId, this.initialPoints, this.symmetryDetected, this.selectedEdge);
      let copied = false;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          copied = true;
        }
      } catch { /* Use the legacy fallback below. */ }
      if (!copied) {
        const helper = document.createElement('textarea');
        helper.value = text;
        helper.style.position = 'fixed'; helper.style.opacity = '0';
        document.body.appendChild(helper); helper.focus(); helper.select();
        copied = document.execCommand('copy');
        helper.remove();
      }
      this.copyButton.textContent = copied ? 'Copied for AI' : 'Copy failed';
      window.setTimeout(() => { this.copyButton.textContent = 'Copy for AI'; }, 1400);
    });
    const toolBar = document.createElement('div');
    toolBar.style.cssText = 'display:flex;gap:5px;align-items:center;';
    const toolDefinitions: Array<{ id: ProfileTool; label: string; title: string }> = [
      { id: 'move', label: '↖ Move', title: 'Move profile points or edges' },
      { id: 'edgeMove', label: '↔ Edge', title: 'Move a complete profile edge' },
      { id: 'fillet', label: '◒ Fillet', title: 'Select a corner, then drag to change fillet radius' },
      { id: 'chamfer', label: '◩ Chamfer', title: 'Select a corner, then drag to change chamfer size' },
    ];
    for (const definition of toolDefinitions) {
      const button = this.makeButton(definition.label, definition.id === 'move' ? '#2563eb' : '#f8fafc');
      button.title = definition.title;
      button.style.flex = '1 1 0';
      button.style.padding = '7px 4px';
      button.addEventListener('click', () => this.setActiveTool(definition.id));
      this.toolButtons.set(definition.id, button);
      toolBar.appendChild(button);
    }
    toolBar.appendChild(this.addPointButton);
    const zoomAllButton = this.makeButton('Zoom all', '#f8fafc');
    zoomAllButton.title = 'Fit the complete profile in the editor';
    zoomAllButton.addEventListener('click', () => {
      this.viewZoom = 1;
      this.viewPanX = 0;
      this.viewPanY = 0;
      this.draw();
    });
    side.append(help, this.readout, toolBar, zoomAllButton, this.sizeInput, this.copyButton);
    body.append(this.canvas, side);
    card.append(header, body);
    this.el.appendChild(card);
    document.body.appendChild(this.el);
    this.el.addEventListener('click', (event) => {
      if (event.target === this.el) this.close();
    });
    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.el.style.display !== 'none') this.close();
    });
    this.canvas.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    this.canvas.addEventListener('pointermove', (event) => this.onPointerMove(event));
    this.canvas.addEventListener('pointerup', (event) => {
      this.draggingId = null; this.draggingEdge = null; this.lastPointer = null;
      this.toolDragStart = null; this.toolDragStartScreen = null; this.panning = false; this.panStart = null;
      if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
    });
    this.canvas.addEventListener('pointerleave', (event) => {
      if (this.canvas.hasPointerCapture(event.pointerId)) return;
      this.draggingId = null; this.draggingEdge = null; this.lastPointer = null;
      this.toolDragStart = null; this.toolDragStartScreen = null; this.panning = false; this.panStart = null;
    });
    this.canvas.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
    this.canvas.addEventListener('mousedown', (event) => {
      if (event.button === 1 || event.button === 2) event.preventDefault();
    });
    this.canvas.addEventListener('auxclick', (event) => event.preventDefault());
    this.canvas.addEventListener('contextmenu', (event) => event.preventDefault());
  }

  private makeButton(label: string, background: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.textContent = label;
    button.style.cssText = `padding:8px 10px;border:1px solid #cbd5e1;border-radius:6px;background:${background};color:#1e293b;font:600 11px var(--mono-font,monospace);cursor:pointer;`;
    return button;
  }

  private setActiveTool(tool: ProfileTool): void {
    this.activeTool = tool;
    for (const [id, button] of this.toolButtons) {
      const active = id === tool;
      button.style.background = active ? '#2563eb' : '#f8fafc';
      button.style.color = active ? '#fff' : '#1e293b';
      button.style.borderColor = active ? '#2563eb' : '#cbd5e1';
    }
    if (tool !== 'move') this.addPointMode = false;
    if (tool === 'fillet' || tool === 'chamfer') {
      const point = this.points.find((item) => item.id === this.selectedId);
      if (point) {
        this.pushHistory();
        point.operation = tool;
        point.size = Math.max(0.2, point.size || 1);
        const mirrorId = this.symmetryDetected ? this.symmetryPairs.get(point.id) : undefined;
        const mirror = mirrorId === undefined ? undefined : this.points.find((item) => item.id === mirrorId);
        if (mirror) { mirror.operation = tool; mirror.size = point.size; }
        this.syncControls();
        this.draw();
      }
    }
  }

  public open(options: ProfileInspectorOpenOptions): void {
    this.options = options;
    const profile = profileFromSegments(options.segments, options.axis);
    this.points = profile.points;
    this.edges = profile.edges;
    this.initialPoints = this.points.map((point) => ({ ...point }));
    this.initialEdges = this.edges.map((edge) => ({ ...edge }));
    this.detectSymmetry();
    this.history = [this.captureSnapshot()];
    this.historyIndex = 0;
    this.updateHistoryButtons();
    this.selectedId = this.points[0]?.id ?? null;
    this.title.textContent = `Profile Inspector — ${options.part} / ${options.axis.toUpperCase()} cut`;
    this.el.style.display = 'flex';
    this.updateBounds();
    this.syncControls();
    this.draw();
  }

  public close(): void {
    this.el.style.display = 'none';
    this.addPointMode = false;
    this.draggingId = null;
    this.draggingEdge = null;
    this.lastPointer = null;
    this.toolDragStart = null;
    this.toolDragStartScreen = null;
    this.setActiveTool('move');
  }

  private reset(): void {
    this.pushHistory();
    this.points = this.initialPoints.map((point) => ({ ...point }));
    this.edges = this.initialEdges.map((edge) => ({ ...edge }));
    this.selectedId = this.points[0]?.id ?? null;
    this.selectedEdge = null;
    this.draggingId = null;
    this.draggingEdge = null;
    this.lastPointer = null;
    this.addPointMode = false;
    this.addPointButton.style.background = '#eff6ff';
    this.addPointButton.style.color = '#1d4ed8';
    this.canvas.style.cursor = 'crosshair';
    this.updateBounds();
    this.syncControls();
    this.draw();
  }

  private captureSnapshot(): ProfileSnapshot {
    return {
      points: this.points.map((point) => ({ ...point })),
      edges: this.edges.map((edge) => ({ ...edge })),
    };
  }

  private pushHistory(): void {
    const snapshot = this.captureSnapshot();
    const previous = this.history[this.historyIndex];
    if (previous && JSON.stringify(previous) === JSON.stringify(snapshot)) return;
    this.history = this.history.slice(0, this.historyIndex + 1);
    this.history.push(snapshot);
    this.historyIndex = this.history.length - 1;
    this.updateHistoryButtons();
  }

  private restoreSnapshot(snapshot: ProfileSnapshot): void {
    this.points = snapshot.points.map((point) => ({ ...point }));
    this.edges = snapshot.edges.map((edge) => ({ ...edge }));
    this.selectedId = this.points[0]?.id ?? null;
    this.selectedEdge = null;
    this.draggingId = null;
    this.draggingEdge = null;
    this.lastPointer = null;
    this.updateBounds();
    this.syncControls();
    this.draw();
    this.updateHistoryButtons();
  }

  private updateHistoryButtons(): void {
    this.undoButton.disabled = this.historyIndex <= 0;
    this.redoButton.disabled = this.historyIndex < 0 || this.historyIndex >= this.history.length - 1;
    this.undoButton.style.opacity = this.undoButton.disabled ? '0.45' : '1';
    this.redoButton.style.opacity = this.redoButton.disabled ? '0.45' : '1';
  }

  private undo(): void {
    if (this.historyIndex <= 0) return;
    this.historyIndex -= 1;
    this.restoreSnapshot(this.history[this.historyIndex]!);
  }

  private redo(): void {
    if (this.historyIndex >= this.history.length - 1) return;
    this.historyIndex += 1;
    this.restoreSnapshot(this.history[this.historyIndex]!);
  }

  private detectSymmetry(): void {
    this.symmetryPairs.clear();
    let pairCount = 0;
    for (const point of this.points) {
      if (Math.abs(point.u) < 0.01) continue;
      const mirror = this.points.find((candidate) => candidate.id !== point.id
        && Math.abs(candidate.u + point.u) < 0.02
        && Math.abs(candidate.v - point.v) < 0.02);
      if (mirror) {
        this.symmetryPairs.set(point.id, mirror.id);
        pairCount += 1;
      }
    }
    this.symmetryDetected = pairCount >= 2 && pairCount * 2 >= this.points.length - 2;
  }

  private updateBounds(): void {
    if (!this.points.length) return;
    const us = this.points.map((point) => point.u);
    const vs = this.points.map((point) => point.v);
    const padU = Math.max((Math.max(...us) - Math.min(...us)) * 0.12, 1);
    const padV = Math.max((Math.max(...vs) - Math.min(...vs)) * 0.12, 1);
    this.bounds = { minU: Math.min(...us) - padU, maxU: Math.max(...us) + padU, minV: Math.min(...vs) - padV, maxV: Math.max(...vs) + padV };
  }

  private toCanvas(u: number, v: number): [number, number] {
    const w = this.canvas.width;
    const h = this.canvas.height;
    const baseX = (u - this.bounds.minU) / (this.bounds.maxU - this.bounds.minU) * (w - 40) + 20;
    const baseY = h - ((v - this.bounds.minV) / (this.bounds.maxV - this.bounds.minV) * (h - 40) + 20);
    return [w / 2 + (baseX - w / 2) * this.viewZoom + this.viewPanX, h / 2 + (baseY - h / 2) * this.viewZoom + this.viewPanY];
  }

  private fromCanvas(x: number, y: number): [number, number] {
    const w = this.canvas.width;
    const h = this.canvas.height;
    const baseX = (x - this.viewPanX - w / 2) / this.viewZoom + w / 2;
    const baseY = (y - this.viewPanY - h / 2) / this.viewZoom + h / 2;
    return [(baseX - 20) / (w - 40) * (this.bounds.maxU - this.bounds.minU) + this.bounds.minU, ((h - baseY) - 20) / (h - 40) * (this.bounds.maxV - this.bounds.minV) + this.bounds.minV];
  }

  private draw(): void {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    for (let x = 20; x < this.canvas.width; x += 24) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, this.canvas.height); ctx.stroke(); }
    for (let y = 20; y < this.canvas.height; y += 24) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(this.canvas.width, y); ctx.stroke(); }
    if (!this.options) return;
    for (const edge of this.edges) {
      const a = this.points.find((point) => point.id === edge.a);
      const b = this.points.find((point) => point.id === edge.b);
      if (!a || !b) continue;
      const start = a.operation === 'none' ? [a.u, a.v] as [number, number] : this.cornerTangent(a, b);
      const end = b.operation === 'none' ? [b.u, b.v] as [number, number] : this.cornerTangent(b, a);
      const ca = this.toCanvas(start[0], start[1]);
      const cb = this.toCanvas(end[0], end[1]);
      ctx.strokeStyle = edge === this.selectedEdge || a.operation !== 'none' || b.operation !== 'none' ? '#f97316' : '#2563eb';
      ctx.lineWidth = edge === this.selectedEdge ? 4 : 2;
      ctx.beginPath(); ctx.moveTo(ca[0], ca[1]); ctx.lineTo(cb[0], cb[1]); ctx.stroke();
    }
    for (const point of this.points) {
      if (point.operation === 'none') continue;
      this.drawCornerOperation(point);
    }
    for (const point of this.points) {
      const [x, y] = this.toCanvas(point.u, point.v);
      if (point.id === this.selectedId) {
        const rect = this.canvas.getBoundingClientRect();
        const halfWidth = 7 * this.canvas.width / Math.max(rect.width, 1);
        const halfHeight = 7 * this.canvas.height / Math.max(rect.height, 1);
        ctx.fillStyle = '#f97316';
        ctx.fillRect(x - halfWidth, y - halfHeight, halfWidth * 2, halfHeight * 2);
        ctx.strokeStyle = '#7c2d12';
        ctx.lineWidth = 2;
        ctx.strokeRect(x - halfWidth, y - halfHeight, halfWidth * 2, halfHeight * 2);
      } else {
        const rect = this.canvas.getBoundingClientRect();
        const halfWidth = 4 * this.canvas.width / Math.max(rect.width, 1);
        const halfHeight = 4 * this.canvas.height / Math.max(rect.height, 1);
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(x - halfWidth, y - halfHeight, halfWidth * 2, halfHeight * 2);
      }
      ctx.fillStyle = '#334155'; ctx.font = '11px monospace'; ctx.fillText(`P${point.id}`, x + 8, y - 8);
    }
  }

  private drawCornerOperation(point: ProfilePoint): void {
    const neighbours = this.cornerNeighbours(point);
    if (neighbours.length < 2) return;
    const first = neighbours[0]!;
    const second = neighbours[1]!;
    const firstPoint = this.cornerTangent(point, first);
    const secondPoint = this.cornerTangent(point, second);
    const firstCanvas = this.toCanvas(firstPoint[0], firstPoint[1]);
    const secondCanvas = this.toCanvas(secondPoint[0], secondPoint[1]);
    const pointCanvas = this.toCanvas(point.u, point.v);
    this.ctx.strokeStyle = '#f97316';
    this.ctx.lineWidth = 3;
    this.ctx.beginPath();
    this.ctx.moveTo(firstCanvas[0], firstCanvas[1]);
    if (point.operation === 'fillet') {
      this.ctx.quadraticCurveTo(pointCanvas[0], pointCanvas[1], secondCanvas[0], secondCanvas[1]);
    } else {
      this.ctx.lineTo(secondCanvas[0], secondCanvas[1]);
    }
    this.ctx.stroke();
  }

  private cornerNeighbours(point: ProfilePoint): ProfilePoint[] {
    return this.edges
      .filter((edge) => edge.a === point.id || edge.b === point.id)
      .map((edge) => edge.a === point.id ? edge.b : edge.a)
      .map((id) => this.points.find((candidate) => candidate.id === id))
      .filter((candidate): candidate is ProfilePoint => Boolean(candidate));
  }

  private cornerTangent(point: ProfilePoint, neighbour: ProfilePoint): [number, number] {
    const neighbours = this.cornerNeighbours(point);
    const shortest = neighbours.reduce((value, item) => Math.min(value, Math.hypot(item.u - point.u, item.v - point.v)), Number.POSITIVE_INFINITY);
    const effectiveSize = Math.max(0.05, Math.min(point.size, shortest * 0.45));
    const length = Math.max(Math.hypot(neighbour.u - point.u, neighbour.v - point.v), 0.0001);
    const ratio = effectiveSize / length;
    return [point.u + (neighbour.u - point.u) * ratio, point.v + (neighbour.v - point.v) * ratio];
  }

  private nearestPoint(x: number, y: number): ProfilePoint | null {
    let nearest: ProfilePoint | null = null;
    let distance = 14;
    for (const point of this.points) {
      const canvasPoint = this.toCanvas(point.u, point.v);
      const d = Math.hypot(canvasPoint[0] - x, canvasPoint[1] - y);
      if (d < distance) { distance = d; nearest = point; }
    }
    return nearest;
  }

  private nearestEdge(x: number, y: number): { edge: ProfileEdge; distance: number } | null {
    let nearest: { edge: ProfileEdge; distance: number } | null = null;
    for (const edge of this.edges) {
      const a = this.points.find((point) => point.id === edge.a);
      const b = this.points.find((point) => point.id === edge.b);
      if (!a || !b) continue;
      const ca = this.toCanvas(a.u, a.v);
      const cb = this.toCanvas(b.u, b.v);
      const dx = cb[0] - ca[0];
      const dy = cb[1] - ca[1];
      const lengthSquared = dx * dx + dy * dy;
      const t = lengthSquared > 0 ? Math.max(0, Math.min(1, ((x - ca[0]) * dx + (y - ca[1]) * dy) / lengthSquared)) : 0;
      const distance = Math.hypot(ca[0] + t * dx - x, ca[1] + t * dy - y);
      if (distance <= 12 && (!nearest || distance < nearest.distance)) nearest = { edge, distance };
    }
    return nearest;
  }

  private snapPoint(point: ProfilePoint): void {
    const canvasPoint = this.toCanvas(point.u, point.v);
    let nearest: ProfilePoint | null = null;
    let distance = 10;
    for (const candidate of this.points) {
      if (candidate.id === point.id) continue;
      const candidateCanvas = this.toCanvas(candidate.u, candidate.v);
      const candidateDistance = Math.hypot(candidateCanvas[0] - canvasPoint[0], candidateCanvas[1] - canvasPoint[1]);
      if (candidateDistance < distance) { distance = candidateDistance; nearest = candidate; }
    }
    if (nearest) { point.u = nearest.u; point.v = nearest.v; }
  }

  private onPointerDown(event: PointerEvent): void {
    event.preventDefault();
    this.canvas.setPointerCapture(event.pointerId);
    const rect = this.canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) * this.canvas.width / rect.width;
    const y = (event.clientY - rect.top) * this.canvas.height / rect.height;
    const panGesture = event.button === 1 || event.button === 2 || (event.button === 0 && event.shiftKey);
    if (panGesture) {
      this.panning = true;
      this.panStart = [event.clientX, event.clientY];
      this.canvas.style.cursor = 'grabbing';
      return;
    }
    if (this.addPointMode) {
      const projection = this.projectToNearestSegment(x, y);
      if (!projection || projection.distance > 18) {
        this.readout.textContent = 'Click gần một cạnh profile để snap và thêm điểm.';
        return;
      }
      this.pushHistory();
      const id = Math.max(0, ...this.points.map((item) => item.id)) + 1;
      this.points.push({ id, u: projection.point[0], v: projection.point[1], operation: 'none', size: 1 });
      const edgeIndex = this.edges.indexOf(projection.edge);
      if (edgeIndex >= 0) {
        this.edges.splice(edgeIndex, 1, { a: projection.edge.a, b: id }, { a: id, b: projection.edge.b });
      }
      if (this.symmetryDetected) {
        const mirrorEdge = this.findMirroredEdge(projection.edge);
        if (mirrorEdge && mirrorEdge !== projection.edge) {
          const mirrorId = id + 1;
          this.points.push({ id: mirrorId, u: -projection.point[0], v: projection.point[1], operation: 'none', size: 1 });
          const mirrorIndex = this.edges.indexOf(mirrorEdge);
          if (mirrorIndex >= 0) {
            this.edges.splice(mirrorIndex, 1, { a: mirrorEdge.a, b: mirrorId }, { a: mirrorId, b: mirrorEdge.b });
          }
          this.symmetryPairs.set(id, mirrorId);
          this.symmetryPairs.set(mirrorId, id);
        }
      }
      this.selectedId = id;
      this.addPointMode = false;
      this.addPointButton.style.background = '#eff6ff';
      this.addPointButton.style.color = '#1d4ed8';
      this.canvas.style.cursor = 'crosshair';
      this.updateBounds(); this.syncControls(); this.draw();
      return;
    }
    if (this.activeTool === 'edgeMove') {
      const edgeHit = this.nearestEdge(x, y);
      if (!edgeHit) return;
      this.pushHistory();
      this.selectedId = null;
      this.selectedEdge = edgeHit.edge;
      this.draggingEdge = edgeHit.edge;
      this.lastPointer = this.fromCanvas(x, y);
      this.readout.textContent = `Edge P${edgeHit.edge.a} → P${edgeHit.edge.b}\nDragging edge`;
      this.draw();
      return;
    }
    const selected = this.nearestPoint(x, y);
    if (selected) {
      this.pushHistory();
      this.selectedId = selected.id;
      this.selectedEdge = null;
      this.draggingId = selected.id;
      this.lastPointer = this.fromCanvas(x, y);
      if (this.activeTool === 'fillet' || this.activeTool === 'chamfer') {
        selected.operation = this.activeTool;
        this.toolDragStart = [x, y];
        this.toolDragStartScreen = [event.clientX, event.clientY];
        this.toolStartSize = selected.size;
      }
      this.syncControls(); this.draw();
      return;
    }
    const edgeHit = this.nearestEdge(x, y);
    if (this.activeTool === 'move' && edgeHit) {
      this.pushHistory();
      this.selectedId = null;
      this.selectedEdge = edgeHit.edge;
      this.draggingEdge = edgeHit.edge;
      this.lastPointer = this.fromCanvas(x, y);
      this.readout.textContent = `Edge P${edgeHit.edge.a} → P${edgeHit.edge.b}\nSelected for move`;
      this.draw();
    }
  }

  private projectToNearestSegment(x: number, y: number): { point: [number, number]; distance: number; edge: ProfileEdge } | null {
    let best: { point: [number, number]; distance: number; edge: ProfileEdge } | null = null;
    for (const edge of this.edges) {
      const aPoint = this.points.find((point) => point.id === edge.a);
      const bPoint = this.points.find((point) => point.id === edge.b);
      if (!aPoint || !bPoint) continue;
      const a: [number, number] = [aPoint.u, aPoint.v];
      const b: [number, number] = [bPoint.u, bPoint.v];
      const ca = this.toCanvas(a[0], a[1]);
      const cb = this.toCanvas(b[0], b[1]);
      const dx = cb[0] - ca[0];
      const dy = cb[1] - ca[1];
      const lengthSquared = dx * dx + dy * dy;
      const t = lengthSquared > 0 ? Math.max(0, Math.min(1, ((x - ca[0]) * dx + (y - ca[1]) * dy) / lengthSquared)) : 0;
      const projectedX = ca[0] + t * dx;
      const projectedY = ca[1] + t * dy;
      const distance = Math.hypot(projectedX - x, projectedY - y);
      if (!best || distance < best.distance) {
        best = { point: this.fromCanvas(projectedX, projectedY), distance, edge };
      }
    }
    return best;
  }

  private findMirroredEdge(edge: ProfileEdge): ProfileEdge | null {
    const mirrorA = this.symmetryPairs.get(edge.a);
    const mirrorB = this.symmetryPairs.get(edge.b);
    if (mirrorA === undefined || mirrorB === undefined) return null;
    return this.edges.find((candidate) =>
      (candidate.a === mirrorA && candidate.b === mirrorB)
      || (candidate.a === mirrorB && candidate.b === mirrorA)) ?? null;
  }

  private onPointerMove(event: PointerEvent): void {
    if (this.panning && this.panStart) {
      const dx = event.clientX - this.panStart[0];
      const dy = event.clientY - this.panStart[1];
      this.viewPanX += dx * this.canvas.width / this.canvas.getBoundingClientRect().width;
      this.viewPanY += dy * this.canvas.height / this.canvas.getBoundingClientRect().height;
      this.panStart = [event.clientX, event.clientY];
      this.draw();
      return;
    }
    if (this.draggingId === null && this.draggingEdge === null) return;
    const rect = this.canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) * this.canvas.width / rect.width;
    const y = (event.clientY - rect.top) * this.canvas.height / rect.height;
    const current = this.fromCanvas(x, y);
    if (!this.lastPointer) { this.lastPointer = current; return; }
    const deltaU = current[0] - this.lastPointer[0];
    const deltaV = current[1] - this.lastPointer[1];
    if (this.draggingId !== null) {
      const point = this.points.find((item) => item.id === this.draggingId);
      if (!point) return;
      if ((this.activeTool === 'fillet' || this.activeTool === 'chamfer') && this.toolDragStartScreen) {
        point.operation = this.activeTool;
        const verticalDrag = this.toolDragStartScreen[1] - event.clientY;
        const sizePerPixel = 0.03;
        point.size = Math.max(0.2, Math.min(50, this.toolStartSize + verticalDrag * sizePerPixel));
        const mirrorId = this.symmetryDetected ? this.symmetryPairs.get(point.id) : undefined;
        const mirror = mirrorId === undefined ? undefined : this.points.find((item) => item.id === mirrorId);
        if (mirror) { mirror.operation = point.operation; mirror.size = point.size; }
        this.lastPointer = current;
        this.syncControls();
        this.draw();
        return;
      }
      point.u += deltaU;
      point.v += deltaV;
      this.snapPoint(point);
      if (this.symmetryDetected) {
        const mirrorId = this.symmetryPairs.get(point.id);
        const mirror = mirrorId === undefined ? undefined : this.points.find((item) => item.id === mirrorId);
        if (mirror) { mirror.u = -point.u; mirror.v = point.v; }
      }
    } else if (this.draggingEdge) {
      const a = this.points.find((point) => point.id === this.draggingEdge!.a);
      const b = this.points.find((point) => point.id === this.draggingEdge!.b);
      if (!a || !b) return;
      a.u += deltaU; a.v += deltaV;
      b.u += deltaU; b.v += deltaV;
      if (this.symmetryDetected) {
        const mirrorAId = this.symmetryPairs.get(a.id);
        const mirrorBId = this.symmetryPairs.get(b.id);
        const mirrorA = mirrorAId === undefined ? undefined : this.points.find((point) => point.id === mirrorAId);
        const mirrorB = mirrorBId === undefined ? undefined : this.points.find((point) => point.id === mirrorBId);
        if (mirrorA) { mirrorA.u = -a.u; mirrorA.v = a.v; }
        if (mirrorB) { mirrorB.u = -b.u; mirrorB.v = b.v; }
      }
    }
    this.lastPointer = current;
    if (this.draggingId !== null) this.syncControls();
    this.draw();
  }

  private onWheel(event: WheelEvent): void {
    event.preventDefault();
    const rect = this.canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) * this.canvas.width / rect.width;
    const y = (event.clientY - rect.top) * this.canvas.height / rect.height;
    const before = this.fromCanvas(x, y);
    const factor = Math.exp(-event.deltaY * 0.001);
    this.viewZoom = Math.max(0.25, Math.min(8, this.viewZoom * factor));
    const after = this.toCanvas(before[0], before[1]);
    this.viewPanX += x - after[0];
    this.viewPanY += y - after[1];
    this.draw();
  }

  private applySelectedSize(): void {
    const point = this.points.find((item) => item.id === this.selectedId);
    if (!point) return;
    this.pushHistory();
    point.size = Math.max(0, Number(this.sizeInput.value) || 0);
    if (this.activeTool === 'fillet' || this.activeTool === 'chamfer') point.operation = this.activeTool;
    this.syncControls(); this.draw();
  }

  private syncControls(): void {
    const point = this.points.find((item) => item.id === this.selectedId);
    if (!point) { this.readout.textContent = 'No profile point selected.'; return; }
    this.sizeInput.value = String(point.size);
    const before = this.initialPoints.find((item) => item.id === point.id);
    const changed = !before || Math.hypot(point.u - before.u, point.v - before.v) > 0.01 || point.operation !== before.operation || Math.abs(point.size - before.size) > 0.01;
    this.readout.textContent = `P${point.id}\nu = ${formatNumber(point.u)} mm\nv = ${formatNumber(point.v)} mm\noperation = ${point.operation}\nsize = ${formatNumber(point.size)} mm\nchanged = ${changed ? 'yes' : 'no'}`;
  }
}
